'use client'

import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import type { Order, OrderStatus, OrderType } from '@skop-app/lib-domain/types'

import type { SignDocumentTabOptions } from '@/types'
import { Context } from '@/types/grid'
import { useOrder } from '@/services/queries/orders'
import type {
  OrderSelectionApi as QuoteGridApi,
  OrderSelectionNode as QuoteSelectionNode,
  OrderSelectionNodeData as QuoteSelectionNodeData,
} from '@/features/quote/lib/orderProducts'

export type { QuoteGridApi, QuoteSelectionNode, QuoteSelectionNodeData }

export type QuotePrefillCustomer = {
  id: string
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  address?: string
  city?: string
  zipcode?: string
  country?: string
}

type QuoteContextType = {
  api: QuoteGridApi | null
  setApi: React.Dispatch<React.SetStateAction<QuoteGridApi | null>>
  context: Context | null
  setContext: React.Dispatch<React.SetStateAction<Context | null>>
  orderType: OrderType | null
  setOrderType: React.Dispatch<React.SetStateAction<OrderType | null>>
  orderID: string | null
  setOrderID: React.Dispatch<React.SetStateAction<string | null>>
  order: Order | null
  isOrderLoading: boolean
  resetQuote: () => void
  isQuoteReady: boolean
  options: SignDocumentTabOptions
  setOptions: React.Dispatch<React.SetStateAction<SignDocumentTabOptions>>
  customTitle: string
  setCustomTitle: React.Dispatch<React.SetStateAction<string | undefined>>
  successConfirmationModal: boolean
  setSuccessConfirmationModal: React.Dispatch<React.SetStateAction<boolean>>
  page: 'inventory' | 'orders' | null
  setPage: React.Dispatch<React.SetStateAction<'inventory' | 'orders' | null>>
  orderStatus: OrderStatus | null
  setOrderStatus: React.Dispatch<React.SetStateAction<OrderStatus | null>>
  prefillCustomer: QuotePrefillCustomer | null
  setPrefillCustomer: React.Dispatch<
    React.SetStateAction<QuotePrefillCustomer | null>
  >
}

const QuoteContext = createContext<QuoteContextType | undefined>(undefined)

export function QuoteProvider({ children }: { children: ReactNode }) {
  const [api, setApi] = useState<QuoteGridApi | null>(null)
  const [context, setContext] = useState<Context | null>(null)
  const [orderType, setOrderType] = useState<OrderType | null>(null)
  const [orderID, setOrderID] = useState<string | null>(null)
  const [options, setOptions] = useState<SignDocumentTabOptions>({})
  const [customTitle, setCustomTitle] = useState<string>('')
  const [successConfirmationModal, setSuccessConfirmationModal] =
    useState<boolean>(false)
  const [page, setPage] = useState<'inventory' | 'orders' | null>(null)
  const [orderStatus, setOrderStatus] = useState<OrderStatus | null>(null)
  const [prefillCustomer, setPrefillCustomer] =
    useState<QuotePrefillCustomer | null>(null)
  const { data: order, isLoading: isOrderLoading } = useOrder(
    orderID || undefined,
  )

  const resetQuote = useCallback(() => {
    setApi(null)
    setContext(null)
    setOrderType(null)
    setOrderID(null)
    setOptions({})
    setCustomTitle('')
    setSuccessConfirmationModal(false)
    setPage(null)
    setOrderStatus(null)
    setPrefillCustomer(null)
  }, [])

  const isQuoteReady = useMemo(() => {
    return !!(
      api &&
      context &&
      orderType &&
      orderID &&
      order &&
      orderStatus &&
      !isOrderLoading
    )
  }, [api, context, orderType, orderID, order, orderStatus, isOrderLoading])

  const value = useMemo(
    () => ({
      api,
      setApi,
      context,
      setContext,
      orderType,
      setOrderType,
      orderID,
      setOrderID,
      order: order || null,
      isOrderLoading,
      resetQuote,
      isQuoteReady,
      options,
      setOptions,
      customTitle,
      setCustomTitle,
      successConfirmationModal,
      setSuccessConfirmationModal,
      page,
      setPage,
      orderStatus,
      setOrderStatus,
      prefillCustomer,
      setPrefillCustomer,
    }),
    [
      api,
      setApi,
      context,
      setContext,
      orderType,
      setOrderType,
      orderID,
      setOrderID,
      order,
      isOrderLoading,
      resetQuote,
      isQuoteReady,
      options,
      setOptions,
      customTitle,
      setCustomTitle,
      successConfirmationModal,
      setSuccessConfirmationModal,
      page,
      setPage,
      orderStatus,
      setOrderStatus,
      prefillCustomer,
      setPrefillCustomer,
    ],
  )

  return <QuoteContext.Provider value={value}>{children}</QuoteContext.Provider>
}

export const useQuoteContext = () => {
  const context = useContext(QuoteContext)
  if (context === undefined) {
    throw new Error('useQuoteContext must be used within a QuoteProvider')
  }
  return context
}
